'use client'

import { useState } from 'react'
import {
  Users,
  Briefcase,
  DollarSign,
  LayoutDashboard,
  Package,
  Headphones,
  ShieldCheck,
  BarChart2,
  Database,
  Zap,
  Globe,
  Lock,
  CheckCircle2,
} from 'lucide-react'
import { useT } from '@/lib/useT'

// ─── Module definitions ──────────────────────────────────────────────────────

const moduleConfig = [
  { id: 'crm',       labelKey: 'features.sales',           icon: Users,          color: '#1A535A', bg: 'rgba(26,83,90,0.1)'   },
  { id: 'hr',        labelKey: 'features.people',          icon: Briefcase,      color: '#22614A', bg: 'rgba(34,97,74,0.1)'   },
  { id: 'finance',   labelKey: 'features.finance',         icon: DollarSign,     color: '#9C5800', bg: 'rgba(156,88,0,0.1)'   },
  { id: 'projects',  labelKey: 'features.projects',        icon: LayoutDashboard,color: '#22505A', bg: 'rgba(34,80,90,0.1)'   },
  { id: 'inventory', labelKey: 'features.supplyChain',     icon: Package,        color: '#5C554E', bg: 'rgba(92,85,78,0.1)'   },
  { id: 'helpdesk',  labelKey: 'features.support',         icon: Headphones,     color: '#952E27', bg: 'rgba(149,46,39,0.1)'  },
  { id: 'admin',     labelKey: 'features.admin',           icon: ShieldCheck,    color: '#3E3830', bg: 'rgba(62,56,48,0.1)'   },
  { id: 'analytics', labelKey: 'features.intelligence',    icon: BarChart2,      color: '#003F47', bg: 'rgba(0,63,71,0.1)'    },
]

const coreServicesConfig = [
  { icon: Database, labelKey: 'platform.dataLayer' },
  { icon: Zap,      labelKey: 'platform.workflowEngine'    },
  { icon: Globe,    labelKey: 'platform.restApi' },
  { icon: Lock,     labelKey: 'platform.ssoRbac'         },
]

const capabilityKeys = [
  'platform.feature2',
  'platform.feature1',
  'platform.feature4',
  'platform.feature3',
  'platform.feature6',
  'platform.feature5',
  'platform.feature8',
  'platform.feature7',
]

// ─── Module chip ─────────────────────────────────────────────────────────────

function ModuleNode({
  mod,
  active,
  onClick,
  t,
}: {
  mod: (typeof moduleConfig)[number]
  active: boolean
  onClick: () => void
  t: (key: string) => string
}) {
  const Icon = mod.icon
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-2 focus:outline-none group"
      aria-pressed={active}
    >
      <div
        className="flex h-12 w-12 items-center justify-center rounded-[12px] border transition-all duration-[220ms]"
        style={{
          backgroundColor: active ? mod.color : mod.bg,
          borderColor: active ? mod.color : 'transparent',
          boxShadow: active ? `0 8px 24px ${mod.color}44` : 'none',
          transform: active ? 'scale(1.08)' : 'scale(1)',
        }}
      >
        <Icon
          size={20}
          strokeWidth={1.75}
          style={{ color: active ? '#FFFBF7' : mod.color }}
        />
      </div>
      <span
        className="text-[11px] font-semibold text-center leading-tight transition-colors duration-[220ms]"
        style={{ color: active ? mod.color : '#6C7377' }}
      >
        {t(mod.labelKey as any)}
      </span>
    </button>
  )
}

// ─── Architecture diagram ────────────────────────────────────────────────────

function ArchitectureDiagram({ t }: { t: (key: string) => string }) {
  const [activeId, setActiveId] = useState<string | null>(null)
  const active = moduleConfig.find((m) => m.id === activeId) ?? null

  return (
    <div
      className="relative rounded-[18px] border border-[#E2E4E5] bg-white overflow-hidden"
      style={{ boxShadow: '0 8px 16px rgba(10,23,29,0.08), 0 32px 64px rgba(10,23,29,0.10)' }}
    >
      {/* Waveform accent bar */}
      <div
        className="h-1 w-full"
        style={{
          background:
            'linear-gradient(90deg,#E91E8C,#F7941D,#FFE000,#8DC63F,#27AE9A,#2E9BD6)',
        }}
        aria-hidden="true"
      />

      <div className="p-6 flex flex-col gap-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#9EA2A5]">
              {t('platform.architecture')}
            </p>
            <p className="text-sm font-semibold text-[#0A171D] mt-0.5">
              {t('platform.ecosystem')}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-[#22614A]" aria-hidden="true" />
            <span className="text-[11px] text-[#6C7377] font-medium">{t('platform.allSystemsLive')}</span>
          </div>
        </div>

        {/* Core platform band */}
        <div
          className="rounded-[12px] p-4"
          style={{ background: '#003F47' }}
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#99B0B5] mb-3">
            {t('platform.coreServices')}
          </p>
          <div className="grid grid-cols-4 gap-2">
            {coreServicesConfig.map((s) => {
              const SIcon = s.icon
              return (
                <div
                  key={s.labelKey}
                  className="flex flex-col items-center gap-1.5 rounded-[8px] py-3 px-2 bg-white/10"
                >
                  <SIcon size={16} strokeWidth={1.75} className="text-[#FFBD76]" />
                  <span className="text-[10px] font-medium text-[#FFF6E9]/80 text-center leading-tight">
                    {t(s.labelKey as any)}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Connector lines visual */}
        <div className="flex items-center gap-0">
          {moduleConfig.map((m, i) => (
            <div key={m.id} className="flex-1 flex flex-col items-center">
              <div
                className="w-px transition-all duration-[220ms]"
                style={{
                  height: 20,
                  backgroundColor: activeId === m.id ? m.color : '#C5C8C9',
                }}
                aria-hidden="true"
              />
              {i < moduleConfig.length - 1 && (
                <div className="sr-only" />
              )}
            </div>
          ))}
        </div>

        {/* Module layer */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#9EA2A5] mb-3">
            {t('platform.modules')}
          </p>
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
            {moduleConfig.map((mod) => (
              <ModuleNode
                key={mod.id}
                mod={mod}
                active={activeId === mod.id}
                onClick={() => setActiveId(activeId === mod.id ? null : mod.id)}
                t={t}
              />
            ))}
          </div>
        </div>

        {/* Connector lines down */}
        <div className="flex items-center">
          {moduleConfig.map((m) => (
            <div key={m.id} className="flex-1 flex flex-col items-center">
              <div
                className="w-px transition-all duration-[220ms]"
                style={{
                  height: 20,
                  backgroundColor: activeId === m.id ? m.color : '#C5C8C9',
                }}
                aria-hidden="true"
              />
            </div>
          ))}
        </div>

        {/* Detail / info panel */}
        <div
          className="rounded-[12px] border border-[#E2E4E5] p-4 min-h-[72px] transition-all duration-[220ms]"
          style={{ background: '#FFF9F2' }}
        >
          {active ? (
            <div className="flex items-start gap-3">
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px]"
                style={{ background: active.bg }}
              >
                <active.icon size={18} strokeWidth={1.75} style={{ color: active.color }} />
              </div>
              <div className="flex flex-col gap-0.5">
                <p className="text-sm font-semibold" style={{ color: active.color }}>
                  {t(active.labelKey as any)} Module
                </p>
                <p className="text-xs text-[#6C7377] leading-relaxed">
                  {moduleDetails[active.id]}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-xs text-[#9EA2A5] text-center leading-relaxed pt-3">
              {t('platform.moduleInstructions')}
            </p>
          )}
        </div>

        {/* Integration layer */}
        <div
          className="rounded-[12px] border border-dashed border-[#C5C8C9] p-4"
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#9EA2A5] mb-2">
            {t('platform.integrations')}
          </p>
          <div className="flex flex-wrap gap-2">
            {['Zapier', 'Slack', 'WhatsApp', 'SAP', 'Xero', 'Stripe', 'MS Teams', 'Google Workspace'].map((int) => (
              <span
                key={int}
                className="text-[11px] font-medium text-[#6C7377] bg-white border border-[#E2E4E5] rounded-[999px] px-2.5 py-1"
              >
                {int}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

const moduleDetails: Record<string, string> = {
  crm:       'Shares contact & company records with HR (hiring), Finance (invoicing), and Help Desk (support history). Bi-directional sync with deal pipeline and activity feed.',
  hr:        'Pulls employee cost data into Finance, syncs project resource capacity with Projects, and triggers onboarding workflows via the Workflow Engine.',
  finance:   'Subscribes to deal closures from CRM, purchase orders from Inventory, and payroll events from HR. Outputs consolidated P&L to Analytics.',
  projects:  'Draws resources from HR, raises purchase orders in Inventory, and posts time-logged costs directly to Finance project accounts.',
  inventory: 'Receives purchase triggers from Projects and Finance; emits stock-level events to Analytics dashboards and supplier webhook notifications.',
  helpdesk:  'Pulls customer context from CRM contacts, escalation SLAs from Admin config, and posts resolution events to the Workflow Engine.',
  admin:     'Owns SSO, RBAC policies, and audit log streams. All modules read permissions at runtime from the Admin service; no hard-coded roles.',
  analytics: 'Subscribes to event streams from every module. Provides real-time KPI cards, trend charts, and scheduled board-level PDF reports.',
}

// ─── Main section ─────────────────────────────────────────────────────────────

export default function PlatformOverview() {
  const t = useT()
  
  return (
    <section
      id="platform"
      className="py-24 bg-[#FFF6E9]"
      aria-labelledby="platform-heading"
    >
      <div className="mx-auto max-w-[1200px] px-6">
        <div className="flex flex-col lg:flex-row items-start gap-16">

          {/* Left — text */}
          <div className="flex flex-col gap-6 lg:w-5/12 lg:sticky lg:top-24">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#FFBD76]">
              {t('platform.badge')}
            </p>
            <h2
              id="platform-heading"
              className="text-3xl md:text-4xl font-bold text-[#0A171D] tracking-[-0.02em] leading-[1.08] text-balance"
            >
              {t('platform.heading')}
            </h2>
            <p className="text-base md:text-lg text-[#6C7377] leading-relaxed">
              {t('platform.description')}
            </p>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="list">
              {capabilityKeys.map((capKey) => (
                <li key={capKey} className="flex items-start gap-2.5">
                  <CheckCircle2
                    size={17}
                    className="mt-0.5 shrink-0"
                    style={{ color: '#003F47' }}
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                  <span className="text-sm text-[#16242B] leading-relaxed">{t(capKey as any)}</span>
                </li>
              ))}
            </ul>

            <div className="flex gap-3 pt-2">
              <a
                href="#signup"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-[999px] bg-[#003F47] text-[#FFFBF7] text-sm font-semibold hover:bg-[#00343B] active:translate-y-px transition-all duration-[220ms]"
                style={{ boxShadow: '0 12px 32px rgba(0,63,71,0.22)' }}
              >
                {t('platform.demo')}
              </a>
            </div>
          </div>

          {/* Right — architecture diagram */}
          <div className="lg:w-7/12 w-full">
            <ArchitectureDiagram t={t} />
          </div>

        </div>
      </div>
    </section>
  )
}
